import { createContext, useCallback, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { ttsController } from '../services/tts/TtsController';
import type { SpeechItem, SpeechLang, TtsSnapshot } from '../services/tts/types';
import { useSettings } from './SettingsContext';

interface TtsContextValue {
  status: TtsSnapshot;
  /** The language the app should speak in, derived from the display mode. */
  speechLang: SpeechLang;
  speak: (text: string, lang?: SpeechLang, id?: string) => void;
  speakSequence: (items: SpeechItem[]) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  replay: () => void;
  /** False when there is no voice for the language we would speak in. */
  canSpeak: boolean;
  /** False when this particular phrase has nothing that can say it. */
  canSpeakText: (text: string, lang?: SpeechLang) => boolean;
}

const TtsContext = createContext<TtsContextValue | null>(null);

export function TtsProvider({ children }: { children: ReactNode }) {
  const { settings } = useSettings();
  const status = useSyncExternalStore(ttsController.subscribe, ttsController.getSnapshot);

  // Speak Arabic in both Arabic display modes; speak English only in English mode.
  const speechLang: SpeechLang = settings.languageMode === 'en' ? 'en' : 'ar';

  useEffect(() => {
    void ttsController.setEngine(settings.ttsEngine, settings.piperUrl);
  }, [settings.ttsEngine, settings.piperUrl]);

  useEffect(() => {
    ttsController.setSpeed(settings.speechSpeed);
  }, [settings.speechSpeed]);

  useEffect(() => {
    ttsController.setVoices(settings.arabicVoiceUri, settings.englishVoiceUri);
  }, [settings.arabicVoiceUri, settings.englishVoiceUri]);

  const speak = useCallback(
    (text: string, lang?: SpeechLang, id?: string) => {
      ttsController.speak(text, lang ?? speechLang, id);
    },
    [speechLang],
  );

  const speakSequence = useCallback((items: SpeechItem[]) => {
    void ttsController.speakSequence(items);
  }, []);

  const canSpeakText = useCallback(
    (text: string, lang?: SpeechLang) => ttsController.canSpeakText(text, lang ?? speechLang),
    [speechLang],
  );

  /*
   * These four must keep the SAME identity for the whole life of the provider.
   * Screens stop the audio in an effect cleanup — `useEffect(() => () => stop(), [stop])`.
   * If `stop` were rebuilt whenever the speaking status changed, React would run
   * that cleanup as soon as speech began and immediately cancel it, so nothing
   * would ever be heard.
   */
  const pause = useCallback(() => ttsController.pause(), []);
  const resume = useCallback(() => ttsController.resume(), []);
  const stop = useCallback(() => ttsController.stop(), []);
  const replay = useCallback(() => {
    void ttsController.replay();
  }, []);

  const value = useMemo<TtsContextValue>(
    () => ({
      status,
      speechLang,
      speak,
      speakSequence,
      pause,
      resume,
      stop,
      replay,
      canSpeak: speechLang === 'ar' ? status.hasArabicVoice : status.hasEnglishVoice,
      canSpeakText,
    }),
    [status, speechLang, speak, speakSequence, pause, resume, stop, replay, canSpeakText],
  );

  return <TtsContext.Provider value={value}>{children}</TtsContext.Provider>;
}

export function useTts(): TtsContextValue {
  const ctx = useContext(TtsContext);
  if (!ctx) throw new Error('useTts must be used inside <TtsProvider>');
  return ctx;
}
